import { Injectable, Injector, effect, untracked } from "@angular/core";
import { NavigationExtras, Router } from "@angular/router";
import { SessionDataService } from "./SessionDataService.service";
import { ReloadComponentService } from "./ReloadComponentService.service";
import { BrowserStorageLocalService } from "./BrowserStorageServices/BrowserStorageLocalService.service";
import { SessionData } from "../../interfaces/SessionGlobalInterface.type";
import { Client } from "../../interfaces/ClientGlobalInterface.type";
import { Agrupation } from "../../interfaces/AgrupationGlobalInterface.type";
import { Entity } from "../../interfaces/EntityGlobalInterface.type";

// Navigation types used to determine which routes should be kept when the corresponding type changes (client, entity, agrupation).
enum NAVIGATION_TYPE {
  AGRUPATION = "agrupation",
  ENTITY = "entity",
  CLIENT = "client"
}

// Routes that should not trigger a redirection when the corresponding type changes (client, entity, agrupation).
const ROUTES_WITHOUT_REDIRECTION: Record<NAVIGATION_TYPE, readonly string[]> = {
  agrupation: [
    "/gateways",
    "/gestion-datos/",
    "/dispositivos/listado/interno",
    "/dispositivos/listado/desactivados",
    "/dispositivos/listado/sustituciones",
    "/dispositivos/listado/sustituciones-pendientes",
    "/listado/dispositivos-ignorados",
    "/alarmas/entidad/plantilla",
    "/cobertura/mapa",
    "/cobertura/test",
    "/contraste-cobertura/",
    "/estado-red/entidad",
    "/usuarios/log/agrupacion",
    "/estado-red/kpis",
    "/sensorica/fugas",
    "/analisis-datos/modelos-analiticos/",
    "/ficheros/aqualia",
    "/ficheros/plantillas/listado",
    "/mantenimiento/estado-comunicaciones"
  ],
  entity: [
    "/mantenimiento/global/alarmas",
    "/mantenimiento/global/incidencias",
    "/mantenimiento/entidad/gateways",
    "/mantenimiento/entidad/gateways-cliente",
    "/mantenimiento/entidad/concentradores",
    "/mantenimiento/entidad/optimizar-red",
    "/mantenimiento/entidad/editar-agrupaciones",
    "/mantenimiento/construccion-de-red",
    "/mantenimiento/global/config-externa/",
    "/entidades/listado",
    "/listado/fabricantes-mbus/",
    "/listado/desconocidos-mbus",
    "/gateways/listado/en-stock",
    "/gateways/listado/disponibles",
    "/ficheros/descarga",
    "/ficheros/plantillas"
  ],
  client: [
    "/telecontrol",
    "/mantenimiento/global/gateways",
    "/mantenimiento/global/concentradores",
    "/mantenimiento/global/cola-comandos",
    "/mantenimiento/global/activaciones",
    "/mantenimiento/global/busqueda-global",
    "/mantenimiento/global/config-externa/",
    "/mantenimiento/global/cups-multiples-dispositivos",
    "/mantenimiento/global/usuarios",
    "/mantenimiento/global/usuarios-cliente",
    "/mantenimiento/global/eventos-usuario",
    "/mantenimiento/global/reporte-comunicaciones",
    "/usuarios/log/global",
    "/mantenimiento/configurar/cron",
    "/mantenimiento/configurar/fabricantes",
    "/mantenimiento/monitorizacion/fiscalizador-editar",
    "/mantenimiento/monitorizacion/fiscalizador-mostrar",
    "/mantenimiento/entidad/borrado-datos",
    "/mantenimiento/configurar/ip",
    "/alarmas/global/listado",
    "/listado/bateria-baja",
    "/analisis-datos/consultas",
    "/ficheros/horarios",
    "/estado-red/global",
    "/estado-red/resumen-global"
  ]
};

const MAIN_PAGE = "/principal-agrupacion"; // Main page route used for navigation when session data is loaded and ids match.

/** Keeps routes in sync with session context and navigates to the start URL on load. */
@Injectable()
export class RouteCheckService {
  // Flag to indicate if it's the first load of the application. Used to determine whether to navigate to the startUrl on the first load.
  firstLoad: boolean = true;

  constructor(
    private readonly BrowserStorageLocalService: BrowserStorageLocalService,
    private readonly injector: Injector,
    private readonly ReloadComponentService: ReloadComponentService,
    private readonly router: Router,
    private readonly SessionDataService: SessionDataService
  ) {}

  // Tracks session-scope changes and restores the initial route after session data loads.
  subscribeToNavigation(startUrl: string, state?: NavigationExtras): void {
    let previousClient: Client | null = null;
    let previousEntity: Entity | null = null;
    let previousAgrupation: Agrupation | null = null;

    effect(
      () => {
        const client = this.SessionDataService.client();
        const entity = this.SessionDataService.entity();
        const agrupation = this.SessionDataService.agrupation();
        const loading = this.SessionDataService.sessionLoadingFlag();
        const avoidReload = this.SessionDataService.avoidNavigationReload();

        // Parent/child selections settle in separate navbar effects. Route only the
        // completed scope, not the intermediate combinations or a future selection.
        if (
          !client ||
          (entity
            ? !client.entityList.some(item => item.id === entity.id)
            : client.entityList.length > 0) ||
          (agrupation
            ? agrupation.entity !== entity?.id
            : !!entity?.agrupations.length)
        ) {
          return;
        }

        const clientChanged = client !== previousClient;
        const entityChanged = entity !== previousEntity;
        const agrupationChanged = agrupation !== previousAgrupation;
        previousClient = client;
        previousEntity = entity;
        previousAgrupation = agrupation;

        if (loading) {
          if (agrupation) {
            untracked(() => this.checkSessionLoadFinished(startUrl, state));
          }
          return;
        }
        if (avoidReload) {
          if (!untracked(() => this.SessionDataService.chatbotNavigating())) {
            this.SessionDataService.sendAvoidNavigationReload(false);
          }
          return;
        }
        if (!entity || !agrupation) return;
        if (!clientChanged && !entityChanged && !agrupationChanged) return;
        if (this.stayOnRoute(NAVIGATION_TYPE.CLIENT)) return;

        if (clientChanged) {
          this.router.navigate([MAIN_PAGE]);
        } else if (entityChanged) {
          const currentPath = this.router.url.split("?")[0].split("#")[0];
          const sharedGateways = entity.sharedGateways ?? client.sharedGateways;
          if (
            currentPath.includes("/gateways/detalle/")
              ? sharedGateways
              : this.stayOnRoute(NAVIGATION_TYPE.ENTITY)
          ) {
            this.ReloadComponentService.reload();
          } else {
            this.router.navigate([MAIN_PAGE]);
          }
        } else if (this.stayOnRoute(NAVIGATION_TYPE.ENTITY)) {
          return;
        } else if (this.firstLoad) {
          this.firstLoad = false;
          this.router.navigate([startUrl], state || undefined);
        } else if (this.stayOnRoute(NAVIGATION_TYPE.AGRUPATION)) {
          this.ReloadComponentService.reload();
        } else {
          this.router.navigate([MAIN_PAGE]);
        }
      },
      { injector: this.injector }
    );
  }

  // Returns true if the current route should be kept when the given type changes (client, entity, agrupation).
  stayOnRoute(type: NAVIGATION_TYPE): boolean {
    const entity = untracked(() => this.SessionDataService.entity());
    const client = untracked(() => this.SessionDataService.client());
    const globalManagement = entity?.globalManagement ?? client?.globalManagement;
    const currentPath = this.router.url.split("?")[0].split("#")[0];
    // Special case for the "/gestion-datos/configuracion-cliente" route, which should only be kept when the type is "entity" and globalManagement is true.
    if (currentPath.includes("/gestion-datos/configuracion-cliente")) {
      return type === NAVIGATION_TYPE.ENTITY && globalManagement;
    }
    // Check if the current route is in the list of routes that should be kept when the given type changes (client, entity, agrupation).
    const routeMatches = ROUTES_WITHOUT_REDIRECTION[type]?.some((route: string) => {
      return currentPath.includes(route);
    });
    return !!routeMatches;
  }

  // Checks if the session data has finished loading and navigates to the startUrl if it has.
  checkSessionLoadFinished(startUrl: string, state?: NavigationExtras): void {
    const sessionData = this.BrowserStorageLocalService.getJsonValue("session") as
      SessionData | null | undefined;

    if (!sessionData || typeof sessionData !== "object" || !sessionData.current) {
      this.SessionDataService.sendSessionLoadingFlag(false);
      this.firstLoad = false;
      this.router.navigate([startUrl], state || undefined);
      return;
    }

    // Session values
    const client: Client = sessionData.current.client;
    const agrupation: Agrupation = sessionData.current.agrupation;
    const entity: Entity = sessionData.current.entity;
    // Check loaded values
    const clientLoaded =
      !client || client?.clientId === untracked(() => this.SessionDataService.client())?.clientId;
    const entityLoaded =
      !entity || entity?.id === untracked(() => this.SessionDataService.entity())?.id;
    const agrupationLoaded =
      !agrupation || agrupation?.id === untracked(() => this.SessionDataService.agrupation())?.id;
    // If all values are loaded, navigate to the startUrl and set the session loading flag to false
    if (clientLoaded && entityLoaded && agrupationLoaded) {
      this.SessionDataService.sendSessionLoadingFlag(false);
      this.firstLoad = false;
      this.router.navigate([startUrl], state || undefined);
    }
  }
}
